import { addEdge } from '@xyflow/react';
import type { CanvasState, SliceCreator } from '../types';
import { pushHistory } from '../utils';
import type { SliceCtx } from '../ctx';
import { computeConnectionMeta } from '@features/flows/utils/connection';

type SliceKeys = 'patchEdge' | 'removeEdge' | 'connectEdge';

export const createEdgesSlice: SliceCreator<Pick<CanvasState, SliceKeys>> = (set, get) => {
  const resolveCtx = (): SliceCtx | undefined => (get as any).__ctx;
  const ensureCtx = () => {
    const ctx = resolveCtx();
    if (!ctx) throw new Error('canvas ctx missing for edges slice');
    return ctx;
  };

  return {
    patchEdge: (edgeId, patch) => {
      const { queueInference, debugLog } = ensureCtx();
      debugLog('patchEdge', { edgeId, patch });
      set((state) => {
        pushHistory(state);
        state.graph.edges = state.graph.edges.map((edge) =>
          edge.id === edgeId ? ({ ...edge, data: { ...(edge.data as any), ...patch } } as any) : edge,
        );
      });
      queueInference({ immediate: true });
    },
    removeEdge: (edgeId) => {
      const { queueInference, bumpStructureVersion } = ensureCtx();
      set((state) => {
        pushHistory(state);
        const prevLength = state.graph.edges.length;
        state.graph.edges = state.graph.edges.filter((edge) => edge.id !== edgeId);
        if (state.graph.edges.length !== prevLength) {
          bumpStructureVersion(state);
        }
        if (state.ui.selection.edges.has(edgeId)) state.ui.selection.edges.delete(edgeId);
      });
      queueInference({ immediate: true });
    },
    connectEdge: (params: any) => {
      const { queueInference, bumpStructureVersion, debugLog } = ensureCtx();
      debugLog('connectEdge', params);
      let newEdgeId: string | undefined = params?.id;
      set((state) => {
        const sourceNode = state.graph.nodes.find((node) => node.id === params?.source);
        const targetNode = state.graph.nodes.find((node) => node.id === params?.target);

        const meta = computeConnectionMeta({
          params,
          pending: state.ui.pendingConnect ?? undefined,
          sourcePins: (sourceNode?.data as any)?.pins,
          targetPins: (targetNode?.data as any)?.pins,
        });

        if (!newEdgeId) {
          const srcId = params.source ?? 'src';
          const tgtId = params.target ?? 'tgt';
          newEdgeId = `e-${srcId}-${tgtId}-${meta.rawSource ?? 'out'}-${meta.rawTarget ?? 'in'}-${meta.edgeChannel}`;
        }
        pushHistory(state);
        const prevLength = state.graph.edges.length;
        state.graph.edges = addEdge(
          {
            ...params,
            sourceHandle: meta.qualifiedSourceHandle,
            targetHandle: meta.qualifiedTargetHandle,
            id: newEdgeId,
            data: {
              ...(params.data ?? {}),
              channel: meta.edgeChannel,
              rawSourceHandle: meta.rawSource,
              rawTargetHandle: meta.rawTarget,
              sourceCoarse: meta.sourceCoarseAtConnect,
              targetCoarse: meta.targetCoarseAtConnect,
              typeLabel: meta.typeLabel,
            },
          } as any,
          state.graph.edges,
        );
        if (state.graph.edges.length !== prevLength) {
          bumpStructureVersion(state);
        }
        state.ui.pendingConnect = null;
      });
      queueInference({ immediate: true });
      // Centralized trigger (single path) — mapping slice owns retries.
      try { if (newEdgeId) get().afterEdgeConnected(newEdgeId); } catch { /* noop */ }
      return newEdgeId;
    },
  };
};
